/**
 * @Module: formatAmount
 * @Status: Ready
 * @Summary: Convert decimal amounts to human-readable cooking fractions
 * @Description: Maps common cooking decimals (0.25, 0.333, 0.5, etc.) to Unicode
 *   fraction characters (1/4, 1/3, 1/2). Handles whole+fraction combos (1.5 -> "1 1/2").
 *   Falls back to one-decimal display for non-standard fractions.
 *   Single source of truth -- every component that renders an ingredient amount imports this.
 */

const FRACTION_MAP: [number, string][] = [
  [0.125, '\u215B'], // 1/8
  [0.25,  '\u00BC'], // 1/4
  [0.333, '\u2153'], // 1/3
  [0.375, '\u215C'], // 3/8
  [0.5,   '\u00BD'], // 1/2
  [0.667, '\u2154'], // 2/3
  [0.75,  '\u00BE'], // 3/4
  [0.875, '\u215E'], // 7/8
];

export function formatAmount(amount: number): string {
  if (amount === 0) return '';

  const whole = Math.floor(amount);
  const frac = amount - whole;

  // Check if the fractional part matches a common cooking fraction (within 0.02 tolerance)
  if (frac > 0.01) {
    for (const [decimal, symbol] of FRACTION_MAP) {
      if (Math.abs(frac - decimal) < 0.02) {
        return whole > 0 ? `${whole} ${symbol}` : symbol;
      }
    }
  }

  // Whole number
  if (frac < 0.01) return whole.toString();

  // Non-standard fraction -- show as decimal
  return amount % 1 === 0 ? amount.toString() : amount.toFixed(1);
}

export function formatAmountWithUnit(amount: number, unit: string): string {
  if (amount === 0) return unit || 'to taste';
  const amtStr = formatAmount(amount);
  return unit ? `${amtStr} ${unit}` : amtStr;
}
